import React, { useMemo } from 'react';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import Conditional from '../../common/conditional';
import CustomUTMBreakdown from '../custom-utm/custom-utm-breakdown';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomVisualInspectionSummary from '../custom-utm/custom-utm-summary';
import CustomUTMReportPDF from '../custom-utm/custom-utm-report';
import CustomLRUTSummary from './lrut-summary';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import LRUTGeneralInformationPDF from './lrut-general-information';
import LRUTWaveItems from './lrut-wave-items';
import OverviewSheet from '../custom-general/overview-sheet';
import PipingAnalytics from '../custom-visual-inspection/report-items/pipingAnalytics';

import { LRUTDataItem } from './lrutDataItems';
import { LRUTGeneralInformation } from '../../forms/lrut/lrutGeneralInformation';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
	coverData: models.ExxonMobilFVIPipingCoverData;
	drawing: models.PDFImageProps[];
	photoRecord: models.PDFImageProps[];
	projectDetails: models.Project;
	dataItems: LRUTDataItem[];
	generalInfo: LRUTGeneralInformation;
	pipingItems: models.FVIPipingItem[];
	utInspectionItems: models.UTMInspection[];
	utInspectionOverview: models.CustomUTMOverview;
    executiveSummary: string;
	reportTitle: string;
}

const CustomLRUTReport: React.FC<Props> = ({
	coverData,
	dataItems,
	drawing,
    executiveSummary,
	photoRecord,
    pipingItems,
	generalInfo,
	utInspectionItems,
	utInspectionOverview,
	projectDetails,
	reportTitle,
}) => {
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. Guided Wave Test Procedure',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Visual Testing Procedure',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '4. Rope Access Code of Practice',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '5. RusselSmith Test Procedure',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const anomalyItems = useMemo(() => {
		return pipingItems.flatMap((item) => item.anomalyItems);
	}, [pipingItems]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(projectDetails.inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<CustomVisualInspectionSummary anomalyItems={anomalyItems} />

            <CustomLRUTSummary lrutItems={dataItems} />

			<Conditional isVisible={utInspectionItems.length > 0}>
				<CustomUTMBreakdown
					corrosionAllowance={utInspectionOverview.corrosionAllowance}
					inspectionItems={utInspectionItems} 
					/>
			</Conditional>

			<LRUTGeneralInformationPDF generalInformation={generalInfo} />

			<PipingAnalytics items={pipingItems} />

			<LRUTWaveItems dataItems={dataItems} />

			<CustomUTMReportPDF
				utInspectionItems={utInspectionItems}
				inspectionOverview={utInspectionOverview}
				tmlLocations={drawing}
				title='LRUT Collar & TML Location'
			/>

			<CustomGeneralPhotos label="Photographic Record" photos={photoRecord} />
		</React.Fragment>
	);
};

export default withPDF(CustomLRUTReport);
